import AlertType from "fleet/constant/alert-type";
import PropTypes from "prop-types";
import React from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { DeleteModal } from "ui/dialog-modal/delete-modal";
import { NavLoader } from "ui/loader";
import { Table, Column } from "ui/table/index";
import { sortByProp } from "util/sort-by-prop";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";
import { VIEW } from "view/views";

class AlertListPage extends React.PureComponent {
  static get propTypes() {
    return {
      alerts: PropTypes.array.isRequired,
      deleteAlert: PropTypes.func.isRequired,
      isAdmin: PropTypes.bool.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      users: PropTypes.array.isRequired,
      v3: PropTypes.bool.isRequired,
    };
  }

  constructor() {
    super();

    this.tableOpts = {
      sortName: "id",
      sortOrder: "desc",
    };

    this.state = {
      deleteModalId: undefined,
    };

    this._deleteAlert = this._deleteAlert.bind(this);
    this._onCloseDeleteModal = this._onCloseDeleteModal.bind(this);
    this._formatOperations = this._formatOperations.bind(this);
    this._formatTag = this._formatTag.bind(this);
    this._formatTargets = this._formatTargets.bind(this);
    this._formatThreshold = this._formatThreshold.bind(this);
    this._formatUser = this._formatUser.bind(this);
  }

  static _sortNotificationOnly(a, b, order) {
    return sortByProp("notification_only", order, a, b);
  }

  static _formatNotificationOnly(notificationOnly) {
    return notificationOnly ? (
      <i
        className="fa fa-times text-danger"
        title="This alert does not create triggered alerts"
      />
    ) : (
      <i
        className="fa fa-check text-success"
        title="This alert creates triggered alerts"
      />
    );
  }

  _formatTargets(targets, _alert) {
    return (
      <ul className="list-unstyled">
        {Object.values(targets).map((t) => {
          const linkType = t.type === "d" ? "device" : "tag";
          const unique = `${t.type}-${t.id}`;
          return (
            <li key={unique} data-qa={unique}>
              {t.is_deleted ? (
                <div>{`${linkType}: ${t.name}`}</div>
              ) : (
                <Link to={`/${linkType}s/${t.id}`}>
                  {`${linkType}: ${t.name}`}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  _formatOperations(value, alert) {
    const _show = () => {
      this.setState({ deleteModalId: alert.id });
    };

    const editLinkPrefix = this.props.v3 ? `/diagnostics/alerts` : `/alerts`;
    const editLink = `${editLinkPrefix}/${alert.id}/${VIEW.EDIT}`;

    return (
      <div key={`alert-ops-${alert.id}`}>
        <Link className="btn btn-default btn-sm" to={editLink}>
          <i className="fa fa-edit" /> Edit
        </Link>
        &nbsp;
        <Button size="sm" onClick={_show} variant="secondary">
          <i className="fa fa-times" /> Delete
        </Button>
      </div>
    );
  }

  _deleteAlert() {
    const { deleteModalId } = this.state;
    const { deleteAlert } = this.props;
    return deleteAlert(deleteModalId);
  }

  _onCloseDeleteModal() {
    this.setState({ deleteModalId: undefined });
  }

  _formatUser(userIds, row) {
    const users = this.props.users.filter((u) => userIds.includes(u.id));

    const allUsers = users.map((user) => `${user.name} <${user.email}>`);

    return (
      <ul className="list-unstyled">
        {allUsers.map((u) => (
          <li key={u}>{u}</li>
        ))}
      </ul>
    );
  }

  _formatTag(tag) {
    return <Link to={`/tags/${tag.id}`}>Tag: {tag.name}</Link>;
  }

  _formatType(type, alert) {
    return AlertType.getAlertDesc(type);
  }

  _formatThreshold(params, alert) {
    switch (alert.type) {
      case AlertType.DEVICE_OFFLINE:
        return `${params.offline_threshold} minutes`;
      case AlertType.DISK_USAGE:
        return `${params.disk_usage} %`;
      default:
        return "N/A";
    }
  }

  _renderColumns() {
    const { isAdmin, v3 } = this.props;
    let columns = [];

    if (v3) {
      columns = [
        <Column key="alertName" dataField="name" dataSort={true}>
          Name
        </Column>,
        <Column
          key="notification_only"
          dataAlign="center"
          dataField="notification_only"
          dataSort={true}
          sortFunc={this.constructor._sortNotificationOnly}
          dataFormat={this.constructor._formatNotificationOnly}
          width="140px"
        >
          Triggered Alerts
        </Column>,
        <Column
          key="targets"
          dataField="target_devices"
          dataSort={true}
          dataFormat={this._formatTargets}
        >
          Target
        </Column>,
      ];
    } else {
      columns = [
        <Column
          key="type"
          dataField="type"
          dataSort={true}
          dataFormat={this._formatType}
        >
          Type
        </Column>,
        <Column
          key="tag"
          dataField="tag"
          dataSort={true}
          dataFormat={this._formatTag}
        >
          Target
        </Column>,
        <Column
          key="threshold"
          dataAlign="center"
          dataField={v3 ? "time_range" : "params"}
          dataSort={true}
          dataFormat={this._formatThreshold}
          width={v3 ? "100px" : "150"}
        >
          Threshold
        </Column>,
      ];
    }

    columns = columns.concat([
      <Column key="id" dataField="id" isKey={true} hidden={true}>
        ID
      </Column>,
      <Column
        key="user_ids"
        dataField="user_ids"
        dataSort={true}
        dataFormat={this._formatUser}
      >
        Users
      </Column>,
    ]);

    if (isAdmin) {
      columns.push(
        <Column
          key="operations"
          dataField=""
          dataSort={false}
          dataFormat={this._formatOperations}
          width="150px"
        >
          Operations
        </Column>,
      );
    }

    return columns;
  }

  _renderView() {
    const { alerts, storesLoaded } = this.props;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    return (
      <div>
        <Table
          data={alerts}
          hover={true}
          options={this.tableOpts}
          pagination={true}
          search={false}
        >
          {this._renderColumns()}
        </Table>
      </div>
    );
  }

  render() {
    const { deleteModalId } = this.state;
    const view = this._renderView();

    return (
      <Page title="Alert List">
        <Box noHeader={true}>{view}</Box>
        <DeleteModal
          objectType="alert"
          deleteId={deleteModalId}
          onDelete={this._deleteAlert}
          onClose={this._onCloseDeleteModal}
        />
      </Page>
    );
  }
}

export { AlertListPage };
